// APIKeyModal.tsx - Cloud Platform API Key Input Modal
// Spec 14: Multi-Cloud Integration
// Metronic modal component, API key input, validation

import { AlertCircle, CheckCircle, Eye, EyeOff, Loader, X } from 'lucide-react'
import { useState } from 'react'

interface APIKeyModalProps {
  isOpen: boolean
  onClose: () => void
  platform: {
    id: string
    name: string
  }
  onConnect: (credentials: Record<string, string>) => Promise<void>
}

export default function APIKeyModal({ isOpen, onClose, platform, onConnect }: APIKeyModalProps) {
  const [apiKey, setApiKey] = useState('')
  const [accountId, setAccountId] = useState('')
  const [showApiKey, setShowApiKey] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  if (!isOpen) {
    return null
  }

  const needsAccountId = ['cloudflare', 'aws'].includes(platform.id)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const credentials: Record<string, string> = {}

      // Platform'a gore credentials olustur
      switch (platform.id) {
        case 'cloudflare':
          credentials.api_token = apiKey
          credentials.account_id = accountId
          break
        case 'aws':
          credentials.access_key_id = apiKey
          credentials.secret_access_key = accountId
          break
        case 'gcp':
          credentials.service_account_json = apiKey
          break
        case 'azure':
          credentials.subscription_id = apiKey
          credentials.tenant_id = accountId
          break
        case 'gdrive':
        case 'onedrive':
          credentials.access_token = apiKey
          break
      }

      await onConnect(credentials)
      setSuccess(true)
      setTimeout(() => {
        onClose()
        setSuccess(false)
        setApiKey('')
        setAccountId('')
      }, 1500)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Baglanti basarisiz')
    } finally {
      setLoading(false)
    }
  }

  const getFieldLabels = () => {
    switch (platform.id) {
      case 'cloudflare':
        return { key: 'API Token', account: 'Account ID' }
      case 'aws':
        return { key: 'Access Key ID', account: 'Secret Access Key' }
      case 'gcp':
        return { key: 'Service Account JSON', account: '' }
      case 'azure':
        return { key: 'Subscription ID', account: 'Tenant ID' }
      case 'gdrive':
      case 'onedrive':
        return { key: 'OAuth Access Token', account: '' }
      default:
        return { key: 'API Key', account: 'Account ID' }
    }
  }

  const labels = getFieldLabels()

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
    >
      <div
        className="card w-full max-w-md"
        style={{
          background: 'var(--card-bg)',
          borderRadius: '8px',
          padding: '24px',
          maxHeight: '90vh',
          overflow: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            {platform.name} Baglantisi
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 transition-colors"
            style={{ color: 'var(--text-secondary)' }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Success Message */}
        {success && (
          <div
            className="flex items-center gap-2 p-3 rounded mb-4"
            style={{ background: 'var(--success-bg)', color: 'var(--success-color)' }}
          >
            <CheckCircle size={20} />
            <span>Baglanti basarili!</span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div
            className="flex items-center gap-2 p-3 rounded mb-4"
            style={{ background: 'var(--danger-bg)', color: 'var(--danger-color)' }}
          >
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={(e) => {
          e.preventDefault()
          void handleSubmit(e)
        }}>
          {/* API Key Input */}
          <div className="mb-4">
            <label
              className="block text-sm font-semibold mb-2"
              style={{ color: 'var(--text-primary)' }}
            >
              {labels.key}
            </label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="w-full px-3 py-2 pr-10 rounded"
                style={{
                  background: 'var(--input-bg)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)'
                }}
                placeholder={`${labels.key} girin`}
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
                style={{ color: 'var(--text-secondary)' }}
              >
                {showApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Account ID Input (sadece bazi platformlar) */}
          {needsAccountId && labels.account && (
            <div className="mb-4">
              <label
                className="block text-sm font-semibold mb-2"
                style={{ color: 'var(--text-primary)' }}
              >
                {labels.account}
              </label>
              <input
                type="text"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                className="w-full px-3 py-2 rounded"
                style={{
                  background: 'var(--input-bg)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)'
                }}
                placeholder={`${labels.account} girin`}
                required
                disabled={loading}
              />
            </div>
          )}

          {/* Info Text */}
          <div
            className="p-3 rounded mb-4 text-sm"
            style={{ background: 'var(--info-bg)', color: 'var(--info-color)' }}
          >
            <p>
              API key&apos;iniz guvenli bir sekilde saklanacak ve sadece platform baglantisi icin kullanilacak.
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 rounded font-semibold transition-colors"
              style={{
                background: 'var(--secondary-bg)',
                color: 'var(--text-primary)'
              }}
              disabled={loading}
            >
              Iptal
            </button>
            <button
              type="submit"
              className="flex-1 py-2 px-4 rounded font-semibold transition-colors flex items-center justify-center gap-2"
              style={{
                background: loading ? 'var(--secondary-bg)' : 'var(--primary-color)',
                color: 'white',
                opacity: loading ? 0.7 : 1
              }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader size={18} className="animate-spin" />
                  <span>Dogrulanıyor...</span>
                </>
              ) : (
                'Baglan'
              )}
            </button>
          </div>
        </form>

        {/* Tutorial Link */}
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => window.open(`/cloud/tutorial/${platform.id}`, '_blank')}
            className="text-sm"
            style={{ color: 'var(--primary-color)', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            {platform.name} API key nasil alinir?
          </button>
        </div>
      </div>
    </div>
  )
}
